import type { ApiKeyCreateInput, ApiKeyUpdateInput } from '@atlas/models'
import type { ApiKeyCreatedDTO, ApiKeyDTO } from '@atlas/types'
import { apiClient } from './api.client'

/**
 * The caller's own API keys.
 *
 * Every route is `/users/me/...` rather than `/users/{userId}/...`, so another
 * user's id is not expressible here — the owner comes from the session. All four
 * require an interactive session: a key cannot manage keys, so these calls fail
 * if the app is ever driven by one.
 *
 * `plaintextKey` appears on the create response and nowhere else. It is not
 * stored, cached, or re-fetchable — the UI shows it once and the user copies it.
 */

/** `GET /users/me/api-keys` returns `{ data: [...] }`. */
interface ApiKeyListResponse {
	data: ApiKeyDTO[]
}

export const apiKeyApi = {
	listApiKeys: () =>
		apiClient
			.get<ApiKeyListResponse>('/users/me/api-keys')
			.then((r) => (Array.isArray(r.data?.data) ? r.data.data : [])),

	// The one response carrying a plaintext, and the only chance to read it.
	createApiKey: (body: ApiKeyCreateInput) =>
		apiClient.post<ApiKeyCreatedDTO>('/users/me/api-keys', body).then((r) => r.data),

	// DELETE, not a PATCH to a status: revoking is terminal and a key is never reactivated.
	revokeApiKey: (apiKeyId: string) => apiClient.delete<ApiKeyDTO>(`/users/me/api-keys/${apiKeyId}`).then((r) => r.data),

	// The label, and nothing else a key's authority depends on.
	renameApiKey: (apiKeyId: string, body: ApiKeyUpdateInput) =>
		apiClient.patch<ApiKeyDTO>(`/users/me/api-keys/${apiKeyId}`, body).then((r) => r.data),
}
